import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispute } from '../../context/DisputeContext';
import { saveParameters } from '../../utils/api';
import ProgressBar from '../../components/ProgressBar';

const ParametersPage = () => {
  const navigate = useNavigate();
  const { dispute, updateDispute } = useDispute();

  // Get claimed amount from assessment answers
  const getClaimedAmount = () => {
    if (!dispute || !dispute.assessment_data) return 5000;
    const data = JSON.parse(dispute.assessment_data);
    return parseFloat(data.amount_claimed || data.repair_cost || data.debt_amount || data.amount_owed || 5000);
  };

  const claimedAmount = getClaimedAmount();

  const [minAcceptable, setMinAcceptable] = useState(Math.round(claimedAmount * 0.7));
  const [idealAmount, setIdealAmount] = useState(claimedAmount);
  const [paymentTerms, setPaymentTerms] = useState('immediate');
  const [customPaymentDays, setCustomPaymentDays] = useState(30);
  const [willingToNegotiate, setWillingToNegotiate] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dispute || !dispute.id || !dispute.type) {
      navigate('/start');
    }
  }, [dispute, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (minAcceptable > idealAmount) {
      setError('Minimum acceptable amount cannot be higher than ideal amount');
      return;
    }

    if (minAcceptable > 15000 || idealAmount > 15000) {
      setError('Amounts cannot exceed €15,000 (platform limit)');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Calculate payment timeline days from payment terms
      let paymentDays = 0;
      if (paymentTerms === 'immediate') paymentDays = 0;
      else if (paymentTerms === '7_days') paymentDays = 7;
      else if (paymentTerms === '30_days') paymentDays = 30;
      else if (paymentTerms === 'custom') paymentDays = customPaymentDays;

      const data = await saveParameters({
        disputeId: dispute.id,
        partyId: dispute.partyAId,
        parameters: {
          minimumAcceptable: minAcceptable,
          idealAmount: idealAmount,
          paymentTimelineDays: paymentDays,
          paymentPlanAcceptable: willingToNegotiate,
          relationshipValue: null,
          mustHaveTerms: null,
        },
      });

      updateDispute(data);
      navigate('/invite');
    } catch (err) {
      setError(err.message || 'Failed to save parameters. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!dispute) {
    return null;
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <ProgressBar current={3} total={5} phase="Settlement Parameters" />

      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-3">Set Your Settlement Parameters</h1>
        <p className="text-gray-600">
          These parameters are private and help our AI find fair settlement options.
          The other party will never see these numbers.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-6">Settlement Amounts</h2>

          {/* Ideal Amount */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Ideal Settlement Amount
              </label>
              <span className="text-lg font-bold text-primary-600">
                {formatCurrency(idealAmount)}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={15000}
              step={100}
              value={idealAmount}
              onChange={(e) => setIdealAmount(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
              disabled={submitting}
            />
            <p className="text-xs text-gray-500 mt-2">
              What would you consider a fair and ideal settlement?
            </p>
          </div>

          {/* Minimum Acceptable */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Minimum Acceptable Amount
              </label>
              <span className="text-lg font-bold text-secondary-600">
                {formatCurrency(minAcceptable)}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={15000}
              step={100}
              value={minAcceptable}
              onChange={(e) => setMinAcceptable(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-secondary-500"
              disabled={submitting}
            />
            <p className="text-xs text-gray-500 mt-2">
              What is the absolute minimum you would accept to settle?
            </p>
          </div>

          {/* Settlement Range Visualization */}
          <div className="bg-gradient-to-r from-secondary-100 via-yellow-100 to-primary-100 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-gray-600">Your Settlement Zone</span>
              <span className="text-xs font-medium text-gray-600">
                Range: {formatCurrency(idealAmount - minAcceptable)}
              </span>
            </div>
            <div className="relative h-8 bg-white rounded-lg overflow-hidden">
              <div
                className="absolute h-full bg-gradient-to-r from-yellow-300 to-green-400 opacity-50"
                style={{
                  left: `${(minAcceptable / 15000) * 100}%`,
                  width: `${((idealAmount - minAcceptable) / 15000) * 100}%`,
                }}
              />
              <div
                className="absolute top-0 bottom-0 w-1 bg-secondary-600"
                style={{ left: `${(minAcceptable / 15000) * 100}%` }}
              />
              <div
                className="absolute top-0 bottom-0 w-1 bg-primary-600"
                style={{ left: `${(idealAmount / 15000) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Payment Terms */}
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4">Payment Terms</h2>

          <div className="space-y-3">
            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="paymentTerms"
                value="immediate"
                checked={paymentTerms === 'immediate'}
                onChange={(e) => setPaymentTerms(e.target.value)}
                className="mt-1 mr-3"
                disabled={submitting}
              />
              <div>
                <div className="font-medium">Immediate Payment</div>
                <div className="text-sm text-gray-600">Full payment upon settlement agreement</div>
              </div>
            </label>

            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="paymentTerms"
                value="7_days"
                checked={paymentTerms === '7_days'}
                onChange={(e) => setPaymentTerms(e.target.value)}
                className="mt-1 mr-3"
                disabled={submitting}
              />
              <div>
                <div className="font-medium">7 Days</div>
                <div className="text-sm text-gray-600">Payment within one week</div>
              </div>
            </label>

            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="paymentTerms"
                value="30_days"
                checked={paymentTerms === '30_days'}
                onChange={(e) => setPaymentTerms(e.target.value)}
                className="mt-1 mr-3"
                disabled={submitting}
              />
              <div>
                <div className="font-medium">30 Days</div>
                <div className="text-sm text-gray-600">Payment within one month</div>
              </div>
            </label>

            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="paymentTerms"
                value="custom"
                checked={paymentTerms === 'custom'}
                onChange={(e) => setPaymentTerms(e.target.value)}
                className="mt-1 mr-3"
                disabled={submitting}
              />
              <div className="flex-1">
                <div className="font-medium mb-2">Custom Timeline</div>
                {paymentTerms === 'custom' && (
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={customPaymentDays}
                    onChange={(e) => setCustomPaymentDays(parseInt(e.target.value))}
                    className="input w-32"
                    placeholder="Days"
                    disabled={submitting}
                  />
                )}
              </div>
            </label>
          </div>
        </div>

        {/* Negotiation Flexibility */}
        <div className="card mb-6">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={willingToNegotiate}
              onChange={(e) => setWillingToNegotiate(e.target.checked)}
              className="mt-1 mr-3 w-5 h-5 accent-primary-500"
              disabled={submitting}
            />
            <div>
              <div className="font-medium mb-1">I'm willing to negotiate within this range</div>
              <div className="text-sm text-gray-600">
                Our AI will explore settlement options between your minimum and ideal amounts.
                Unchecking this means you'll only accept your ideal amount.
              </div>
            </div>
          </label>
        </div>

        {/* Info Box */}
        <div className="card bg-blue-50 border-blue-200 mb-6">
          <div className="flex items-start">
            <div className="text-2xl mr-3">🔒</div>
            <div>
              <h4 className="font-bold mb-2">Privacy Protection</h4>
              <p className="text-sm text-gray-700 mb-2">
                These parameters are completely private and will never be shown to the other party.
                Our AI uses them to:
              </p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Find fair settlement zones where both parties might agree</li>
                <li>• Suggest realistic settlement amounts based on similar cases</li>
                <li>• Prioritize solutions within your acceptable range</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            type="button"
            onClick={() => navigate('/assessment')}
            className="btn-secondary"
            disabled={submitting}
          >
            Back
          </button>

          <button
            type="submit"
            className="btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Continue to Invitation'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ParametersPage;
